import Vue from 'vue';
import Router from 'vue-router';
import AppModule from '@/store/modules/app';
import { FWAccessControl } from '@/util/AccessControlUtil';
import LifecycleModule from '@/store/modules/lifecycle';
import UserModule from '@/store/modules/user';
import { PartnerStatus } from '@compassdigital/foodworks.api.ts';
import { PartnerOnboardingStatusEnum } from '@compassdigital/foodworks.ts.sdk';

interface IRouterPermission {

}

Vue.use(Router);

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  routes: [
    {
      path: '/',
      redirect: '/home',
      component: () => import(/* webpackChunkName: "home" */ '@/layout/index.vue'),
      children: [
        {
          path: 'home',
          name: 'home',
          component: () => import(/* webpackChunkName: "home" */ '@/views/home/index.vue'),
        },
      ],
    },
    {
      path: '/',
      redirect: '/configuration',
      component: () => import(/* webpackChunkName: "configuration" */ '@/layout/index.vue'),
      children: [
        {
          path: 'configuration',
          name: 'configuration',
          component: () => import(/* webpackChunkName: "configuration" */ '@/views/configuration/index.vue'),
          meta: {
            ac: {
              resource: 'page-configuration',
              attribute: 'configuration',
            },
          },
        },
      ],
    },
    {
      path: '/',
      redirect: '/scheduler',
      component: () => import(/* webpackChunkName: "scheduler" */ '@/layout/index.vue'),
      children: [
        {
          path: 'scheduler',
          name: 'scheduler',
          component: () => import(/* webpackChunkName: "scheduler" */ '@/views/scheduler/index.vue'),
        },
        {
          path: 'scheduler/:marketId',
          name: 'scheduler-market',
          component: () => import(/* webpackChunkName: "scheduler" */ '@/views/scheduler/index.vue'),
        },
        {
          path: 'scheduler/publish-restaurants',
          name: 'publish-restaurants',
          component: () => import(/* webpackChunkName: "scheduler/publish-restaurants" */ '@/views/scheduler/publishRestaurants/index.vue'),
        },
        {
          path: 'scheduler/publish-units',
          name: 'publish-units',
          component: () => import(/* webpackChunkName: "scheduler/publish-units" */ '@/views/scheduler/publishUnits/index.vue'),
        },
      ],
    },
    {
      path: '/partners',
      component: () => import(/* webpackChunkName: "partners" */ '@/layout/index.vue'),
      children: [
        {
          path: '',
          name: 'partners',
          component: () => import(/* webpackChunkName: "partners" */ '@/views/partners/index.vue'),
          meta: {
            ac: {
              resource: 'page-partners',
              attribute: 'partners-list',
            },
          },
        },
        {
          path: 'create-partner',
          name: 'create-partner',
          component: () => import(/* webpackChunkName: "create-partner" */ '@/views/partners/create/index.vue'),
          meta: {
            ac: {
              resource: 'page-partners',
              attribute: 'partners-create',
            },
          },
        },
        {
          path: 'partner_not_found',
          name: 'notfound',
          component: () => import(/* webpackChunkName: "partner_not_found" */ '@/views/NotFound/PartnerNotFound/index.vue'),
        },
        {
          path: 'unit_not_found',
          name: 'unit_not_found',
          component: () => import(/* webpackChunkName: "unit_not_found" */ '@/views/NotFound/UnitNotFound/index.vue'),
        },
        {
          path: ':id',
          component: () => import(/* webpackChunkName: "partnerlayout" */ '@/views/partners/partner.layout.vue'),
          children: [
            {
              path: 'details',
              name: 'partner-detail',
              component: () => import(/* webpackChunkName: "partner-detail/:id" */ '@/views/partners/detail/index.vue'),
              meta: {
                ac: {
                  resource: 'page-partners',
                  attribute: 'partners-detail',
                },
              },
            },
            {
              path: 'documents',
              name: 'document-list',
              component: () => import(/* webpackChunkName: "document-list/:id" */ '@/components/Form/FileUploadForm/PartnerDocumentsList/index.vue'),
              meta: {
                ac: {
                  resource: 'page-partners',
                  attribute: 'partners-document-list',
                },
              },
            },
            {
              path: 'scheduling',
              name: 'schedule-settings-partners',
              component: () => import(/* webpackChunkName: "document-list/:id" */ '@/components/Form/Partner/ScheduleSettingsPartners/index.vue'),
            },
          ],
        },
      ],
    },
    {
      path: '/users',
      redirect: '/users',
      component: () => import(/* webpackChunkName: "users" */ '@/layout/index.vue'),
      children: [
        {
          path: '',
          name: 'users',
          component: () => import(/* webpackChunkName: "users" */ '@/views/users/index.vue'),
          meta: {
            ac: {
              resource: 'page-users',
              attribute: 'user-list',
            },
          },
        },
        {
          path: 'create-user',
          name: 'create-user',
          component: () => import(/* webpackChunkName: "create-user" */ '@/views/users/create/index.vue'),
          meta: {
            ac: {
              resource: 'page-users',
              attribute: 'user-create',
            },
          },
        },
        {
          path: 'edit-user/:id',
          name: 'edit-user/:id',
          component: () => import(/* webpackChunkName: "edit-user/:id" */ '@/views/users/edit/index.vue'),
          meta: {
            ac: {
              resource: 'page-users',
              attribute: 'user-edit',
            },
          },
        },
      ],
    },
    {
      path: '/units',
      redirect: '/units',
      component: () => import(/* webpackChunkName: "units" */ '@/layout/index.vue'),
      children: [
        {
          path: '',
          name: 'units',
          component: () => import(/* webpackChunkName: "units" */ '@/views/units/index.vue'),
          meta: {
            ac: {
              resource: 'page-units',
              attribute: 'unit-list',
            },
          },
        },
        {
          path: 'create-unit',
          name: 'create-unit',
          component: () => import(/* webpackChunkName: "create-unit" */ '@/views/units/create/index.vue'),
          meta: {
            ac: {
              resource: 'page-units',
              attribute: 'unit-create',
            },
          },
        },
        {
          path: 'schedule-settings/:id',
          name: 'schedule-settings',
          component: () => import(/* webpackChunkName: "schedule-settings/:id" */ '@/views/units/schedule-settings/index.vue'),
        },
        {
          path: 'non-workingdays',
          name: 'non-workingdays',
          component: () => import(/* webpackChunkName: "non-workingdays/:id" */ '@/views/units/non-workingdays/index.vue'),
        },
      ],
    },
    {
      path: '/statistics',
      redirect: '/statistics',
      component: () => import(/* webpackChunkName: "statistics" */ '@/layout/index.vue'),
      children: [
        {
          path: '',
          name: 'statistics',
          component: () => import(/* webpackChunkName: "statistics" */ '@/views/statistics/index.vue'),
          children: [
            {
              path: 'user-stats',
              name: 'user-stats',
              component: () => import(/* webpackChunkName: "user-stats" */ '@/components/Stats/UserStats/index.vue'),
              meta: {
                ac: {
                  resource: 'page-statistics',
                  attribute: 'users-statistics',
                },
              },
            },
            {
              path: 'market-stats',
              name: 'market-stats',
              component: () => import(/* webpackChunkName: "market-stats" */ '@/components/Stats/MarketStats/index.vue'),
              meta: {
                ac: {
                  resource: 'page-statistics',
                  attribute: 'market-statistics',
                },
              },
            },
            {
              path: 'document-stats',
              name: 'document-stats',
              component: () => import(/* webpackChunkName: "document-stats" */ '@/components/Stats/DocumentStats/index.vue'),
              meta: {
                ac: {
                  resource: 'page-statistics',
                  attribute: 'document-statistics',
                },
              },
            },
            {
              path: 'scheduler-statistics',
              name: 'scheduler-statistics',
              component: () => import(/* webpackChunkName: "scheduler-statistics" */ '@/components/Stats/SchedulerStats/index.vue'),
              meta: {
                ac: {
                  resource: 'page-statistics',
                  attribute: 'scheduler-statistics',
                },
              },
            },
            {
              path: 'onboarding-stats',
              name: 'onboarding-stats',
              component: () => import(/* webpackChunkName: "onboarding-stats" */ '@/components/Stats/OnboardingStats/index.vue'),
            },
          ],
        },
      ],
    },
    {
      path: '/loading',
      name: 'loading',
      component: () => import(/* webpackChunkName: "login" */ '@/views/application/loading.vue'),
    },
    {
      path: '/login',
      component: () => import(/* webpackChunkName: "login" */ '@/layout/fw/index.vue'),
      children: [
        {
          path: '',
          name: 'login',
          meta: {
            noAuth: true,
          },
          component: () => import(/* webpackChunkName: "login" */ '@/views/login/index.vue'),
        },
        {
          path: 'new_password',
          name: 'new_password',
          meta: {
            noAuth: true,
          },
          component: () => import(/* webpackChunkName: "new_password" */ '@/views/login/set-password/index.vue'),
        },
        {
          path: 'forgot_password',
          name: 'forgot_password',
          meta: {
            noAuth: true,
          },
          component: () => import(/* webpackChunkName: "forgot_password" */ '@/views/login/forgot-password/index.vue'),
        },
        {
          path: 'reset_password',
          name: 'reset_password',
          meta: {
            noAuth: true,
          },
          component: () => import(/* webpackChunkName: "reset_password" */ '@/views/login/reset-password/index.vue'),
        },
      ],
    },
    {
      path: '/error',
      name: 'error',
      component: () => import(/* webpackChunkName: "error" */ '@/views/error/index.vue'),
      meta: {
        noAuth: true,
      },
    },
    {
      path: '/application/forms',
      name: 'application/forms',
      component: () => import(/* webpackChunkName: "application/forms" */ '@/views/application/forms/index.vue'),
      meta: {
        ac: {
          resource: 'page-onboarding',
          attribute: 'details',
        },
      },
    },
    {
      path: '/application/detail_forms',
      name: 'application/detail_forms',
      component: () => import(/* webpackChunkName: "application/detail_forms" */ '@/views/application/detail_forms/index.vue'),

      meta: {
        ac: {
          resource: 'page-onboarding',
          attribute: 'form',
        },
      },
    },
    {
      path: '/dev/components',
      name: 'components',
      component: () => import(/* webpackChunkName: "library" */ '@/views/library/index.vue'),
      meta: {
        noAuth: true,
        // permission: []
      },
    },
    {
      path: '/unit-restaurants',
      component: () => import(/* webpackChunkName: "unit-restaurants" */ '@/layout/index.vue'),
      children: [
        {
          path: '',
          name: 'unit-restaurants',
          component: () => import(/* webpackChunkName: "'unit-restaurants" */ '@/views/unitrestaurants/index.vue'),
          meta: {
            ac: {
              resource: 'page-units-restaurant',
              attribute: 'unit-restaurant-list',
            },
          },
        },
      ],
    },
  ],
});

router.beforeEach(async (to, from, next) => {
  try {
    if (to.name === 'loading' || to.meta.noAuth) {
      return next();
    }
    if ((!LifecycleModule.initialized && AppModule.accessToken) && to.name !== 'loading') {
      return next({
        name: 'loading',
        query: { redirect: to.fullPath },
      });
    }
    // if (AppModule.requiresOnboarding) {
    //   if (!to.fullPath.includes('application')) {
    //     return next('/application/detail_forms');
    //   }
    //   return next();
    // }
    if (!AppModule.authenticated) {
      if (to.meta && to.meta.noAuth) {
        return next();
      }
      return next('/login');
    }

    if (to.meta && to.meta.ac) {
      const { resource, attribute } = to.meta.ac;
      if (FWAccessControl.hasRead(resource, attribute)) { return next(); }
      return next('/home');
    }

    return next();
  } catch (err) {
    console.log(err);
    return next();
  }
});

export default router;
